import { createLogger, logError } from '@core/logging';
import { apiOf, enrichLogEvent } from '@core/swagger-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { serializeError } from 'serialize-error';
import { useDeviceInfo, useSiteSettingsContext } from '../..';

function handleUnhandledRejection(event: PromiseRejectionEvent) {
    // Sadly no additional information
    logError('Unhandled promise rejection: {Reason}', { Reason: serializeError(event.reason), Uri: globalThis.window?.location?.href });

    // Still log to console as well
    console.error('Unhandled Promise Rejection:');
    console.error(`Reason: ${serializeError(event.reason)}`);
}

// The configuration is the same for every render, so the logger is set up once instead of being
// replaced on each one. The server configures its logger at boot (apps/frontend/src/start.ts); the
// browser and the embed do it here. The accounts on a log line used to sit in module variables that
// every render overwrote -- shared by all requests on the server, so a line could name another
// request's account -- and are now looked up per line by enrichLogEvent.
let loggerConfigured = false;

export const CreateLogger = () => {
    const { operatingSystemInformation, deviceType, browserInformation } = useDeviceInfo();
    const isProduction = useSiteSettingsContext().isProduction;
    const api = apiOf(useQueryClient());

    if (!import.meta.env.SSR && !loggerConfigured) {
        loggerConfigured = true;
        createLogger(`${api.baseUrl}/logging/`, isProduction, { browserInformation, deviceType, operatingSystemInformation }, enrichLogEvent);
    }

    useEffect(() => {
        if (!globalThis.window) return;

        // This doesn't use an event handler, so we can just overwrite any existing error handler
        globalThis.window.onerror = function (message, source, lineno, colno, error) {
            logError('Uncaught error occurred: {Message}', {
                Message: message,
                Source: source,
                Line: lineno,
                Column: colno,
                Error: serializeError(error),
                Uri: globalThis.window?.location?.href
            });

            // Still log to console as well
            console.error('Uncaught Error:');
            console.error(`Message: ${message}`);
            console.error(`Source: ${source}`);
            console.error(`Line: ${lineno}`);
            console.error(`Column: ${colno}`);
            console.error(`Error: ${serializeError(error)}`);
            console.error(`Uri: ${globalThis.window?.location?.href}`);
        };

        globalThis.window.addEventListener('unhandledrejection', handleUnhandledRejection);
        return () => {
            globalThis.window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    return <></>;
};
